import pick from 'lodash/pick';
import xor from 'lodash/xor';
import { useMemo } from 'react';
import {
  Rect,
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryLine,
  VictoryScatter,
  VictoryVoronoiContainer,
} from 'victory';

import SalesPipelineSyncStatus from '~/components/audiences/SalesPipelineSyncStatus';
import { StageList } from '~/components/audiences/StageList';
import { StageListSkeleton } from '~/components/audiences/StageListSkeleton';
import { stages as stagesArray } from '~/lib/salesPipeline';

import styles from './styles';

const stagesByKey = stagesArray.reduce((object, stage) => {
  object[stage.key] = stage;

  return object;
}, {});

const ChartGrid = (props) => {
  const { index, x1, x2, y1, y2, style, ticks } = props;

  const first = index === 0;
  const last = index === ticks.length - 1;

  if (first || last) return null;

  return (
    <line
      vectorEffect="non-scaling-stroke"
      role="presentation"
      shapeRendering="auto"
      x1={x1}
      x2={x2}
      y1={y1}
      y2={y2}
      style={style}
    />
  );
};

const ChartPoint = (props) => {
  const { x, y, style, active } = props;

  if (!active) return null;

  return (
    <circle
      cx={x}
      cy={y}
      fill={style.fill}
      stroke="white"
      filter="url('#shadow')"
      r={2}
    />
  );
};

const ChartSkeleton = () => {
  return (
    <div>
      <div className="px-4 pt-4 pb-2 space-y-4">
        <div className="h-48 ck-skeleton"></div>
      </div>
    </div>
  );
};

export const Chart = ({ data }) => {
  const seriesData = useMemo(() => {
    return Object.keys(data).map((stageKey) => ({
      data: data[stageKey].timeseries,
      accent: stagesByKey[stageKey].accent,
      key: stageKey,
      ...data[stageKey],
    }));
  }, [data]);

  const isEmpty =
    seriesData.filter((series) => series.timeseries.length === 0).length ===
    seriesData.length;

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center h-48">
        <span className="font-medium text-center text-gray">No data</span>
      </div>
    );
  }

  return (
    <VictoryChart
      height={100}
      padding={{ top: 0, bottom: 0 }}
      scale={{ x: 'time' }}
      domainPadding={{ y: [0, 5] }}
      minDomain={{ y: -10 }}
      containerComponent={
        <VictoryVoronoiContainer
          voronoiDimension="x"
          voronoiBlacklist={['line']}
        />
      }>
      <VictoryAxis
        gridComponent={<ChartGrid />}
        style={{
          axis: { display: 'none' },
          ticks: { display: 'none' },
          tickLabels: { display: 'none' },
          grid: { stroke: '#e1e9f0' },
        }}
      />
      <Rect
        role="presentation"
        style={{ pointerEvents: 'none', fill: "url('#axis-fade')" }}
      />
      {seriesData.map((series, index) => {
        if (series.enabled == false) {
          return null;
        }

        return (
          <VictoryGroup
            key={index}
            seriesIndex={index}
            data={series.timeseries}
            y="influencedValue"
            x="date">
            <VictoryLine
              name="line"
              style={{
                data: {
                  strokeWidth: 1,
                  filter: `url('#line-shadow-${series.key}')`,
                  stroke: series.accent.hex,
                },
              }}
            />
            <VictoryScatter
              dataComponent={<ChartPoint style={{ fill: series.accent.hex }} />}
            />
          </VictoryGroup>
        );
      })}
    </VictoryChart>
  );
};

export const SalesPipelineSVGDefs = () => {
  return (
    <svg
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        visibility: 'hidden',
      }}>
      <defs>
        {stagesArray.map((stage) => (
          <filter
            id={`line-shadow-${stage.key}`}
            key={stage.key}
            x="-50%"
            y="-10%"
            width="200%"
            height="200%">
            <feDropShadow
              floodColor={stage.accent.hex}
              dx="0"
              stdDeviation="1.5"
              floodOpacity={0.5}
            />
          </filter>
        ))}

        <linearGradient id="line-fade">
          <stop offset="0%" sytle={{ stopColor: 'red', stopOpacity: 1 }} />
          <stop offset="50%" sytle={{ stopColor: 'green', stopOpacity: 0.5 }} />
          <stop offset="100%" sytle={{ stopColor: 'blue', stopOpacity: 0 }} />
        </linearGradient>
        <linearGradient id="axis-fade" gradientTransform="rotate(90)">
          <stop offset="0%" style={{ stopColor: '#fff', stopOpacity: 0 }} />
          <stop offset="80%" style={{ stopColor: '#fff', stopOpacity: 0.8 }} />
          <stop offset="100%" style={{ stopColor: '#fff', stopOpacity: 1 }} />
        </linearGradient>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow
            floodColor="black"
            dx="0"
            dy="0"
            stdDeviation=".5"
            floodOpacity={0.25}
          />
        </filter>
      </defs>
    </svg>
  );
};

const DateWindowMessage = ({ dateWindow, loading }) => {
  let text = '';

  switch (dateWindow) {
    case 'trailing_7_days':
      text = 'Audience performance this week';
      break;
    case 'trailing_14_days':
      text = 'Audience performance the past two weeks';
      break;
    case 'trailing_30_days':
      text = 'Audience performance this month';
      break;
  }

  return (
    <span
      className={classnames('font-medium text-gray-600 text-sm', {
        'ck-skeleton': loading,
      })}>
      {text}
    </span>
  );
};

export const SalesPipeline = ({ classes, loading, data, dateWindow }) => {
  const { stages, status, syncedAt } = data || {};

  const [activeStageKeys, setActiveStageKeys] = useState([]);
  const acceptableStagekeys = Object.keys(stagesByKey);

  let enabledStages = pick(stages, acceptableStagekeys);

  if (activeStageKeys.length !== 0) {
    enabledStages = pick(stages, activeStageKeys);
  }

  const toggleActiveStage = (key) => {
    setActiveStageKeys(xor([key], activeStageKeys));
  };

  return (
    <>
      <div className={classes.salesPipeline}>
        <div className="flex-1">
          {loading ? <ChartSkeleton /> : <Chart data={enabledStages} />}
        </div>
      </div>
      <div className="p-4 pt-0 overflow-auto border-b border-gray-200 space-y-3">
        <DateWindowMessage loading={loading} dateWindow={dateWindow} />
        {loading ? (
          <StageListSkeleton />
        ) : (
          <StageList
            data={stages}
            activeStageKeys={activeStageKeys}
            toggleStage={toggleActiveStage}
          />
        )}
      </div>
      <SalesPipelineSyncStatus
        status={status}
        syncedAt={syncedAt}
        loading={loading}
      />
      <SalesPipelineSVGDefs />
    </>
  );
};

export default withStyles(styles)(SalesPipeline);
